import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShippingTypeToken, PaymentTypeToken } from '@sleed/bizcommerce-api';
import { shippingTypesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type ShippingTypeFormValues } from '~/lib/schemas/shipping-type';

export const shippingTypeKeys = {
	all: ['shipping-types'] as const,
	list: () => [...shippingTypeKeys.all, 'list'] as const,
	detail: (id: number | string) => [...shippingTypeKeys.all, 'detail', id] as const,
	payments: (id: number | string) => [...shippingTypeKeys.all, 'payments', id] as const
};

export function useShippingTypesList() {
	return useQuery<ShippingTypeToken[]>({
		queryKey: shippingTypeKeys.list(),
		queryFn: async () => (await shippingTypesApi.getAll()).data.items ?? []
	});
}

/** Single shipping type by id (full record incl. constraints/description/image). `create` → null. */
export function useShippingType(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<ShippingTypeToken | null>({
		queryKey: shippingTypeKeys.detail(id),
		queryFn: async () => (enabled ? (await shippingTypesApi.getById(numericId)).data : null),
		enabled
	});
}

/**
 * Payment types with an `isChecked` flag indicating which are compatible with the shipping type
 * (legacy `/shippingtypes/getallpayments?id=`). Used by the editor's Payment-methods tab.
 */
export function useShippingTypePayments(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<PaymentTypeToken[]>({
		queryKey: shippingTypeKeys.payments(id),
		queryFn: async () => (enabled ? ((await shippingTypesApi.getAllPayments(numericId)).data.items ?? []) : []),
		enabled
	});
}

/**
 * Upload a shipping-type image into slot `key` ("1"). The shipping type must already exist (id > 0);
 * the File is attached as form-data keyed by the slot subkey (mirrors the manufacturer/payment-type
 * upload + the legacy BinaryRepoImages/UploadShippingType contract).
 */
export async function uploadShippingTypeImage(shippingTypeId: number, key: '1', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadShippingType(shippingTypeId, key, { data: fd });
}

export function useShippingTypeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: shippingTypeKeys.all });
	const create = useMutation({
		mutationFn: async (form: ShippingTypeFormValues) => (await shippingTypesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: ShippingTypeFormValues) => (await shippingTypesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await shippingTypesApi.remove(id)).data, onSuccess: invalidate });
	/** Persist the payment-type compatibility list ({ id: shippingTypeId, options: paymentTypeIds }). */
	const savePayments = useMutation({
		mutationFn: async ({ id, options }: { id: number; options: number[] }) =>
			(await shippingTypesApi.savePaymentsTypes({ id, options })).data,
		onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: shippingTypeKeys.payments(String(vars.id)) })
	});
	return { create, update, remove, savePayments };
}
